import { useEffect, useMemo } from 'react'
import { Box } from '@mui/material'
import { usePrescriptionsQuery } from '../../../../features/prescription'
import { colorTokens } from '../../../../shared/styles/theme'
import { foldByOcrRequestEvent } from '../list/queue'
import { useRemoteInput } from '../remoteInput/useRemoteInput'
import { ImagePane } from './image/ImagePane'
import { ItemsPane } from './ItemsPane'

interface Props {
  ocrRequestEventId: string
  /** 開いていた紙が一覧から消えたとき(遠隔の店が断った・頼む店が取り消した等)。選択を外す */
  onGone: () => void
}

/**
 * 項目ペインの幅。残りが画像に回る(画像左・項目右・常時突合 = ADR-0010)。
 * 紙は縦長なので画像側を広げても左右の余白が増えるだけ。420px から広げ、2 列の版面をここに収める
 * (松田 8/29「画像領域が大きすぎる。結果表示部をもう少し広く」)。
 */
const ITEMS_PANE_WIDTH = 560

/**
 * 読取結果(詳細)。**画像左・項目右の左右分割**で、突合の最中に片方が消えないことが要件
 * (ADR-0010 / ADR-0031。顧客 8/29「拡大すると右の処方情報が消えてしまうため非常に見にくい」)。
 *
 * 左の画像は P4 で埋める。回転・ページ送り・自動傾き補正は共有 hook
 * `usePrescriptionImageBox(images, area, { viewerControls: true })` を呼ぶ(自前で書かない)。
 *
 * 頼まれた紙(遠隔入力)は自店の一覧には無く、5 秒の口(`useRemoteInput`)が持つ最新版から引く。
 * 自店の紙が先で、無ければ頼まれた紙。
 *
 * 開いていた紙がどちらにも無くなったら(遠隔の店が断る・頼む店が取り消すと、頼まれた紙は遠隔の店の
 * 一覧から消える)、選択を外して「処方箋を選んでください」に戻す。詳細に空の面を残さない
 * (design-book `03-patterns/empty-states.md` 型1)。
 */
export const PrescriptionDetailPage = ({
  ocrRequestEventId,
  onGone,
}: Props) => {
  const { data, isPending } = usePrescriptionsQuery()
  const { lookup, isPending: isRemotePending } = useRemoteInput()

  const own = useMemo(
    () =>
      foldByOcrRequestEvent(data ?? []).find(
        (presc) => presc.ocr_request_event_id === ocrRequestEventId
      ),
    [data, ocrRequestEventId]
  )
  const paper = lookup(ocrRequestEventId)
  const prescription =
    own ?? (paper.side === 'remote' ? paper.request.prescription : undefined)

  const isGone = !prescription && !isPending && !isRemotePending
  useEffect(() => {
    if (isGone) onGone()
  }, [isGone, onGone])

  if (!prescription) return null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        height: '100%',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          borderRight: `1px solid ${colorTokens.border.default}`,
        }}
      >
        <ImagePane
          images={prescription.presc_images ?? []}
          imageArea={
            prescription.basic_result?.処方箋存在領域?.selected_option ?? []
          }
        />
      </Box>
      <Box sx={{ width: ITEMS_PANE_WIDTH, flexShrink: 0, overflow: 'auto' }}>
        <ItemsPane prescription={prescription} />
      </Box>
    </Box>
  )
}
